import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BeneficiariesSuccessCodes, BeneficiaryErrorCodes } from '@app/translations';
import { BeneficiaryViewType } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  BeneficiaryDataContext,
  BeneficiaryRequirementsResponse,
  PaginatedResponse,
  PaginationMeta,
  ViewType,
} from '@finxone-platform/shared/sys-config-types';
import { Action, Selector, State, StateContext, Store, createSelector } from '@ngxs/store';
import { Observable, catchError, map, of, take, tap, throwError } from 'rxjs';
import {
  ClearBeneficiaryCache,
  GetBeneficiary,
  GetBeneficiaryRequiredFieldsAction,
  SetBeneficiaryLoadingStatus,
  SetSelectedBeneficiary,
  ToggleBeneficiaryFavouriteStatus,
  UpdateBeneficiary,
  UpdateSelectedBeneficiaryFavoriteStatus,
  UpdateSelectedBeneficiaryStateInfo,
} from '../actions/beneficiary.action';
import { AddProgressBarStack, RemoveProgressBarStack } from '../actions/progress-bar.action';
import { AccountService } from '../services/account-service/account-service.service';
import { GetBeneficiaryResponse } from '../services/account-service/account.type';
import { findAndShowAlertFromCode } from '../utils/alert-code-utils/alert-code.utils';
import { AccountState } from './account.state';
import { ApiActions, UpdateApiIsLoadingAction } from './api-loading.state';

export interface BeneficiaryStateModel {
  isLoading: boolean;
  items: GetBeneficiaryResponse[];
  favoriteItems: GetBeneficiaryResponse[];
  recentPaidItems: GetBeneficiaryResponse[];
  totalPages: number;
  selectedBeneficiary?: string;
  totalResult?: number;
  pageLimit?: number;
  pagination: PaginationMeta;
  beneficiaryRequiredFields: BeneficiaryRequirementsResponse;
}

@State<BeneficiaryStateModel>({
  name: 'beneficiary',
  defaults: {
    isLoading: false,
    items: [],
    favoriteItems: [],
    recentPaidItems: [],
    totalPages: 1,
    beneficiaryRequiredFields: {} as BeneficiaryRequirementsResponse,
    totalResult: 0,
    pagination: {} as PaginationMeta,
  },
})
@Injectable()
export class BeneficiaryState {
  constructor(
    private accountService: AccountService,
    private alertHandlerService: AlertHandlerService,
    private store: Store,
    private router: Router,
  ) {}

  @Selector()
  static getBeneficiary(state: BeneficiaryStateModel): BeneficiaryStateModel {
    return state;
  }

  @Selector()
  static getLoadingStatus(state: BeneficiaryStateModel) {
    return state.isLoading;
  }

  static getBeneficiaryRequiredFields() {
    return createSelector(
      [BeneficiaryState],
      (state: BeneficiaryStateModel): BeneficiaryRequirementsResponse => {
        return state.beneficiaryRequiredFields;
      },
    );
  }

  static getMatchedBeneficiaryById() {
    return createSelector(
      [BeneficiaryState],
      (state: BeneficiaryStateModel): GetBeneficiaryResponse | undefined => {
        // Combine all beneficiaries from different lists into one array for a complete search.
        const allBeneficiaries = [...state.items, ...state.favoriteItems, ...state.recentPaidItems];
        // Use `find` to return the first beneficiary that matches the provided ID.
        return allBeneficiaries.find((beneficiary) => beneficiary.id === state?.selectedBeneficiary);
      },
    );
  }

  @Action(SetBeneficiaryLoadingStatus)
  setLoadingStatus(ctx: StateContext<BeneficiaryStateModel>, action: SetBeneficiaryLoadingStatus) {
    try {
      ctx.patchState({
        ...ctx.getState(),
        isLoading: action.isLoading,
      });
      return true;
    } catch (err) {
      return throwError(() => err);
    }
  }
  @Action(GetBeneficiary)
  fetchBeneficiary(ctx: StateContext<BeneficiaryStateModel>, action: GetBeneficiary) {
    try {
      if (action.isLoaderShown) {
        ctx.dispatch(new AddProgressBarStack({ uniqueId: 'GetBeneficiary' }));
        ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getBeneficiaries, true));
      }
      let selectedAccountId = '';
      if (action?.dataContext !== BeneficiaryDataContext.ALL_BENEFICIARIES) {
        selectedAccountId = this.store.selectSnapshot(AccountState.getCurrentAccountId);
      }

      return this.accountService
        .getBeneficiary(
          action.page,
          action.size,
          action?.searchValue,
          action?.currency,
          action.beneficiaryViewType,
          selectedAccountId,
        )
        .pipe(
          tap((beneficiaryList: PaginatedResponse<GetBeneficiaryResponse>) => {
            const state = ctx.getState();

            const {
              items: prevItems,
              favoriteItems: prevFavoriteItems,
              recentPaidItems: prevRecentPaidItems,
              selectedBeneficiary,
              beneficiaryRequiredFields,
            } = state;

            const shouldReset = action.page === 1 || action.viewType === ViewType.TABLE;

            // Map view type to the corresponding previous list
            const viewTypeToPrevList = {
              [BeneficiaryViewType.ALL_LIST]: prevItems,
              [BeneficiaryViewType.FAVORITE_LIST]: prevFavoriteItems,
              [BeneficiaryViewType.RECENTLY_PAID_LIST]: prevRecentPaidItems,
            } as const;

            const baseList = viewTypeToPrevList[action.beneficiaryViewType] ?? prevItems;

            // Merge beneficiaries depending on pagination/view
            const mergedList = shouldReset
              ? beneficiaryList.result
              : [...baseList, ...beneficiaryList.result];

            const uniqueResults = Array.from(new Map(mergedList.map((item) => [item.id, item])).values());

            // Assign updated lists based on view type
            const updatedItems =
              action.beneficiaryViewType === BeneficiaryViewType.ALL_LIST || !action.beneficiaryViewType
                ? uniqueResults
                : prevItems;

            const updatedFavoriteItems =
              action.beneficiaryViewType === BeneficiaryViewType.FAVORITE_LIST
                ? uniqueResults
                : prevFavoriteItems;

            const updatedRecentPaidItems =
              action.beneficiaryViewType === BeneficiaryViewType.RECENTLY_PAID_LIST
                ? uniqueResults
                : prevRecentPaidItems;

            // Patch state
            ctx.patchState({
              isLoading: false,
              items: updatedItems,
              favoriteItems: updatedFavoriteItems,
              recentPaidItems: updatedRecentPaidItems,
              totalPages: beneficiaryList.totalPages ?? 1,
              totalResult: beneficiaryList.totalItems,
              pageLimit: Number(beneficiaryList.limit),
              selectedBeneficiary: selectedBeneficiary?.length ? selectedBeneficiary : uniqueResults[0]?.id,
              beneficiaryRequiredFields,
              pagination: {
                page: action.page ?? 1,
                totalPages: beneficiaryList.totalPages ?? 1,
                totalItems: beneficiaryList.totalItems ?? 0,
                pageSize: beneficiaryList.limit ?? 10,
              },
            });

            ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetBeneficiary' }));
            ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getBeneficiaries, false));
          }),
          catchError<unknown, Observable<boolean>>((_err) => {
            ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetBeneficiary' }));
            ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getBeneficiaries, false));
            throw _err;
          }),
        );
    } catch (err) {
      ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetBeneficiary' }));
      ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getBeneficiaries, false));
      return throwError(() => err);
    }
  }
  @Action(SetSelectedBeneficiary)
  fetchSelectedBeneficiary(ctx: StateContext<BeneficiaryStateModel>, action: SetSelectedBeneficiary) {
    try {
      const beneficiaryData = ctx.getState();
      return ctx.patchState({
        ...beneficiaryData,
        isLoading: false,
        items: beneficiaryData.items,
        selectedBeneficiary: action.payload,
        beneficiaryRequiredFields: beneficiaryData.beneficiaryRequiredFields,
      });
    } catch (err) {
      return throwError(() => err);
    }
  }
  @Action(UpdateSelectedBeneficiaryStateInfo)
  updateSelectedBeneficiaryStateInfo(
    ctx: StateContext<BeneficiaryStateModel>,
    action: UpdateSelectedBeneficiaryStateInfo,
  ) {
    try {
      const beneficiaryData = ctx.getState();
      const selectedBeneficiaryIndex = beneficiaryData.items.findIndex(
        (v) => v.id === action?.beneficiaryInfo.id,
      );
      if (selectedBeneficiaryIndex !== -1) {
        beneficiaryData.items[selectedBeneficiaryIndex] = action?.beneficiaryInfo;
      }
      return ctx.setState({ ...beneficiaryData });
    } catch (err) {
      return throwError(() => err);
    }
  }

  @Action(UpdateSelectedBeneficiaryFavoriteStatus)
  updateSelectedBeneficiaryFavoriteStatus(
    ctx: StateContext<BeneficiaryStateModel>,
    { isFavorite }: UpdateSelectedBeneficiaryFavoriteStatus,
  ) {
    try {
      const state = ctx.getState();
      const { selectedBeneficiary, items, favoriteItems, recentPaidItems } = state;

      const updateRecords = (list: GetBeneficiaryResponse[]) =>
        list?.map((item) => (item.id === selectedBeneficiary ? { ...item, isFavorite } : item));

      return ctx.setState({
        ...state,
        items: updateRecords(items),
        favoriteItems: updateRecords(favoriteItems),
        recentPaidItems: updateRecords(recentPaidItems),
      });
    } catch (error) {
      console.error('Failed to update selected beneficiary favorite status:', error);
      return throwError(() => error);
    }
  }

  @Action(ToggleBeneficiaryFavouriteStatus)
  toggleBeneficiaryFavoriteStatus(
    ctx: StateContext<BeneficiaryStateModel>,
    { beneficiaryId, isFavorite }: ToggleBeneficiaryFavouriteStatus,
  ) {
    this.accountService
      .updateBeneficiaryFavoriteStatus(beneficiaryId, isFavorite)
      .pipe(
        tap(() => {
          this.updateSelectedBeneficiaryFavoriteStatusLocally(ctx, { beneficiaryId, isFavorite });
        }),
        catchError((err) => {
          console.error('Error updating beneficiary favorite status:', err);
          return of();
        }),
      )
      .subscribe();
  }

  private updateSelectedBeneficiaryFavoriteStatusLocally(
    ctx: StateContext<BeneficiaryStateModel>,
    { beneficiaryId, isFavorite }: ToggleBeneficiaryFavouriteStatus,
  ) {
    const state = ctx.getState();
    const { items, favoriteItems, recentPaidItems } = state;

    const updateRecords = (list: GetBeneficiaryResponse[]) =>
      list?.map((item) => (item.id === beneficiaryId ? { ...item, isFavorite } : item));

    return ctx.setState({
      ...state,
      items: updateRecords(items),
      favoriteItems: updateRecords(favoriteItems),
      recentPaidItems: updateRecords(recentPaidItems),
    });
  }

  @Action(UpdateBeneficiary)
  UpdateBeneficiary(ctx: StateContext<BeneficiaryStateModel>, action: UpdateBeneficiary) {
    // Return the observable so callers can react to completion and perform navigation themselves.
    return this.accountService.updateBeneficiary(action.id, action.payload, action?.accessToken).pipe(
      take(2),
      tap((response) => {
        if (response) {
          // Update state and API loading flag, keep alerts here
          this.store.dispatch(new UpdateSelectedBeneficiaryStateInfo(response));
          this.store.dispatch(new UpdateApiIsLoadingAction(ApiActions.addEditDomesticBeneficiary, false));
          findAndShowAlertFromCode(this.store, this.router, this.alertHandlerService, [
            BeneficiariesSuccessCodes.UPDATE_BENEFICIARY,
          ]);
        }
        // Notify caller with the actual API response (or null if response falsy)
        if (action.onComplete) {
          action.onComplete(response ?? null);
        }
      }),
      map((response) => {
        return response;
      }),
      catchError((error) => {
        this.store.dispatch(new UpdateApiIsLoadingAction(ApiActions.addEditDomesticBeneficiary, false));

        findAndShowAlertFromCode(this.store, this.router, this.alertHandlerService, [
          BeneficiaryErrorCodes.FAILED_TO_UPDATE_BENEFICIARY,
        ]);
        console.error('Error While Editing Beneficiary:', error);

        // Notify caller about the failure
        if (action.onComplete) {
          action.onComplete(null);
        }
        return of(null);
      }),
    );
  }

  @Action(GetBeneficiaryRequiredFieldsAction)
  fetchBeneficiaryRequiredFields(
    ctx: StateContext<BeneficiaryStateModel>,
    action: GetBeneficiaryRequiredFieldsAction,
  ) {
    ctx.dispatch(
      new AddProgressBarStack({
        uniqueId: 'GetBeneficiaryRequiredFieldsAction',
      }),
    );
    ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getBeneficiaryRequiredFields, true));

    return this.accountService
      .getBeneficiaryRequirements(action.countryCode, action.currencyCode, action.scheme)
      .pipe(
        tap((beneficiaryRequireFields) => {
          const beneficiaryData = ctx.getState();
          ctx.setState({
            ...beneficiaryData,
            beneficiaryRequiredFields: beneficiaryRequireFields,
          });

          ctx.dispatch(
            new RemoveProgressBarStack({
              uniqueId: 'GetBeneficiaryRequiredFieldsAction',
            }),
          );
          ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getBeneficiaryRequiredFields, false));
        }),
        catchError<unknown, Observable<boolean>>((_err) => {
          ctx.dispatch(
            new RemoveProgressBarStack({
              uniqueId: 'GetBeneficiaryRequiredFieldsAction',
            }),
          );
          ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getBeneficiaryRequiredFields, false));
          throw _err;
        }),
      );
  }

  @Action(ClearBeneficiaryCache)
  clearAccountCache(ctx: StateContext<BeneficiaryStateModel>) {
    ctx.patchState({ isLoading: false, items: [], totalPages: 1 });
  }
}
